import React, { useState, useMemo } from "react";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import styles from "./dashboards-page.module.scss";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { useI18N } from "@src/language/language-context";
import Spinner from "@src/components/atoms/spinner/spinner";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ButtonWithIcon } from "@components/atoms/buttons/button-with-icon/button-with-icon";
import { DashboardsList } from "@src/components/organisms/dashboards-list/dashboards-list";
import { EmptyDashboardView } from "@src/components/molecules/empty-dashboard-view/empty-dashboard-view";
import { DashboardTileGrid } from "@src/components/molecules/dashboard-tile-grid/dashboard-tile-grid";
import { DashboardCanvasHeader } from "@src/components/molecules/dashboard-canvas-header/dashboard-canvas-header";
import { WidgetLibraryPanel } from "@src/components/organisms/widget-library-panel/widget-library-panel";
import { useDashboards } from "./hooks/use-dashboards";
import { useAutoPromoteDefault } from "./hooks/use-auto-promote-default";
import { useDashboardActions } from "./hooks/use-dashboard-actions";
import { paletteColorsToStyle } from "@components/molecules/dashboard-theme-editor/palette-style";
import { DashboardThemeEditor } from "@components/molecules/dashboard-theme-editor/dashboard-theme-editor";
import { useDashboardColorsEditor } from "@src/hooks/use-dashboard-colors-editor";
import { useErrorSnackbar } from "@src/hooks/use-error-snackbar";
import caretRight from "@src/assets/svg/caret-right.svg";
import plusIcon from "@src/assets/svg/plus-dark-icon.svg";
import cn from "classnames";

const cssClassName = "dashboards-page";

export const DashboardsPage = () => {
  const i18n = useI18N();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLibraryCollapsed, setIsLibraryCollapsed] = useState(true);

  const {
    dashboards,
    selectedDashboardId,
    defaultDashboardId,
    isLoading,
    isDetailsLoading,
    fetchError,
    isCurrentDetailsLoaded,
    listPromise,
  } = useDashboards();
  // Editor-only cold-start behaviour: on a fresh mount (the
  // login / auth-context swap), if the BE has exactly one
  // PUBLISHED dashboard and no default, PATCH that dashboard
  // to be default. The hook awaits the SAME list GET promise
  // that the shared hook fired (we don't issue a second GET),
  // so the read-before-write guarantee is structural: no
  // response = no PATCH. Lives in its own file so the shared
  // `useDashboards` stays read-only and the viewer (`/dashboard`)
  // never imports it.
  useAutoPromoteDefault(listPromise);

  const {
    addDashboard,
    updateDashboard,
    deleteDashboard,
    selectDashboard,
    setDefaultDashboard,
    duplicateDashboard,
    publishDashboard,
  } = useDashboardActions();

  const texts = {
    dashboardsTitle: i18n("pages.dashboardsPage.title"),
    addDashboard: i18n("pages.dashboardsPage.addDashboard"),
    toggleCollapse: i18n("pages.dashboardsPage.toggleCollapse"),
    toggleExpand: i18n("pages.dashboardsPage.toggleExpand"),
  };

  const selectedDashboard = useMemo(
    () => dashboards.find((d) => d.id === selectedDashboardId),
    [dashboards, selectedDashboardId],
  );

  const hasWidgets = useMemo(
    () =>
      selectedDashboard && Object.keys(selectedDashboard.widgets).length > 0,
    [selectedDashboard],
  );

  // Per-dashboard palette: `selectedDashboard.presentation?.colors` is the
  // BE-truth on load. Helper returns `undefined` when no key is set, so
  // unthemed dashboards do not get a `style` prop on the panel at all
  // (SCSS fallbacks paint instead).
  const paletteStyle = useMemo(
    () =>
      selectedDashboard
        ? paletteColorsToStyle(selectedDashboard.presentation?.colors)
        : undefined,
    [selectedDashboard],
  );

  // Theme editor hooks. Pulled into a separate hook file so the
  // debounce + flush + revert machinery stays testable (Phase 7 spec).
  // Hook needs the dashboard id; gates in JSX ensure it's truthy when
  // the editor mounts.
  const themeEditor = useDashboardColorsEditor({
    dashboardId: selectedDashboardId ?? "",
    currentColors: selectedDashboard?.presentation?.colors ?? null,
  });

  const handleAddDashboard = async (
    name: string,
    description: string | null,
  ) => {
    const created = await addDashboard(name, description);
    if (created) {
      // Auto-select the new dashboard using its server-assigned id
      selectDashboard(created.id);
    }
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  // One toast per distinct failure, carrying the message the backend returned
  // (parsed by `parseAxiosApiError` in the hook). `fetchError` covers both
  // failing GETs: the dashboards list and a single dashboard's details. The
  // canvas shows the translated generic copy instead of this text, because it
  // has to read the same for either failure and in every locale.
  useErrorSnackbar(fetchError?.message ?? null);

  const renderRightPanel = () => {
    if (!selectedDashboard) {
      // Both "the list came back without dashboards" and "the list has
      // dashboards but none is selected" land here and share the same
      // affordance: the left column lists the dashboards and offers
      // "Add dashboard", so "create or select" is true either way. A failed
      // fetch is different, because then we don't know whether any dashboard
      // exists — showing "create or select" would claim the request
      // succeeded.
      if (fetchError) {
        return <EmptyDashboardView variant="error" />;
      }
      return <EmptyDashboardView variant="no-selection" />;
    }

    // A failed fetch reaches the canvas unless a dashboard is already loaded
    // for this selection:
    //   - `source === "detail"` — this selection's own GET failed, so there is
    //     nothing to show for it.
    //   - any source with nothing loaded and nothing in flight — that is the
    //     list failing while a stale selection's detail also failed, where the
    //     error is the only thing left to show. Without this the spinner would
    //     turn forever, because no fetch is coming to clear it.
    // A failed LIST fetch on its own keeps an already-loaded dashboard in front
    // of the user, and is reported through the toast instead.
    //
    // This branch sits BEFORE the loading spinner so a definite failure
    // surfaces the error immediately, even in non-batched sequences where the
    // spinner flag could otherwise win a one-frame race against the error flag.
    if (
      fetchError &&
      (fetchError.source === "detail" ||
        (!isDetailsLoading && !isCurrentDetailsLoaded))
    ) {
      return <EmptyDashboardView variant="error" />;
    }

    // Pre-fetch placeholder. Two flags are combined here:
    //   - `!isCurrentDetailsLoaded` — read synchronously from the hook
    //     each render; true on the very first render after a click
    //     (before the useEffect has fired) up until a successful detail
    //     GET has populated `loadedDetailIds`. Without this, the empty
    //     Summary from the List endpoint would briefly fall through to
    //     the empty-dashboard placeholder for one frame.
    //   - `isDetailsLoading` — flips true inside the effect once a fetch
    //     is actually in flight; redundant with the synchronous flag
    //     during the in-flight window, but kept here for explicit
    //     "fetch is happening right now" semantics that future callers
    //     can rely on without having to know about the ref.
    if (isDetailsLoading || !isCurrentDetailsLoaded) {
      return (
        <div className={styles[`${cssClassName}__spinner-container`]}>
          <Spinner />
        </div>
      );
    }

    if (hasWidgets) {
      return <DashboardTileGrid dashboard={selectedDashboard} />;
    }

    return <EmptyDashboardView variant="empty-dashboard" />;
  };

  return (
    <MainContainer>
      <RoundBorderComponentContainer>
        <div className={styles[cssClassName]}>
          <div className={styles[`${cssClassName}__inner-container`]}>
            <>
              <div
                className={cn(styles[`${cssClassName}__left-column`], {
                  [styles[`${cssClassName}__left-column--collapsed`]]:
                    isCollapsed,
                })}
              >
                <div className={styles[`${cssClassName}__left-panel`]}>
                  <SubHeader variant={"less-padding"}>
                    <FlexRowContainer
                      justifyContent={isCollapsed ? "center" : "space-between"}
                    >
                      {!isCollapsed ? (
                        <>
                          <SubTitle text={texts.dashboardsTitle} />
                          <FlexRowContainer justifyContent="flex-end">
                            <ButtonWithIcon
                              fun={handleAddClick}
                              icon={plusIcon}
                              text={texts.addDashboard}
                              variant={["primary", "fit-content", "no-wrap"]}
                              disabled={isCreating}
                            />

                            <span
                              className={cn(
                                styles[`${cssClassName}__collapse-caret`],
                                {
                                  [styles[
                                    `${cssClassName}__collapse-caret--expanded`
                                  ]]: !isCollapsed,
                                },
                              )}
                            >
                              <IconButton
                                fun={handleToggleCollapse}
                                icon={caretRight}
                              />
                            </span>
                          </FlexRowContainer>
                        </>
                      ) : (
                        <span
                          className={cn(
                            styles[`${cssClassName}__collapse-caret`],
                            {
                              [styles[
                                `${cssClassName}__collapse-caret--expanded`
                              ]]: !isCollapsed,
                            },
                          )}
                        >
                          <IconButton
                            fun={handleToggleCollapse}
                            icon={caretRight}
                          />
                        </span>
                      )}
                    </FlexRowContainer>
                  </SubHeader>
                  <div
                    className={`${styles[`${cssClassName}__list-container`]} ${
                      isCollapsed
                        ? styles[`${cssClassName}__list-container--collapsed`]
                        : ""
                    }`}
                  >
                    <DashboardsList
                      dashboards={dashboards}
                      selectedDashboardId={selectedDashboardId}
                      defaultDashboardId={defaultDashboardId}
                      isCollapsed={isCollapsed}
                      isCreating={isCreating}
                      isLoading={isLoading}
                      onSelectDashboard={selectDashboard}
                      onRenameDashboard={updateDashboard}
                      onDuplicateDashboard={duplicateDashboard}
                      onDeleteDashboard={deleteDashboard}
                      onSetDefaultDashboard={setDefaultDashboard}
                      onConfirmCreate={handleAddDashboard}
                      onCancelCreate={handleCancelCreate}
                    />
                  </div>
                </div>
              </div>

              <div
                className={styles[`${cssClassName}__central-panel`]}
                style={paletteStyle}
                // Anchor used by `useThemeColorsForCharts` to find the
                // cascade root. CSS variables cascade DOWN, so any
                // descendant (chart widget, tile) inherits them; the
                // hook just needs a single DOM element to read vars
                // off — this one is the natural pick.
                data-theme-anchor=""
              >
                {selectedDashboard && (
                  <DashboardCanvasHeader
                    dashboard={selectedDashboard}
                    onPublish={publishDashboard}
                  />
                )}
                <div className={styles[`${cssClassName}__canvas`]}>
                  {renderRightPanel()}
                </div>
                {/* Theme editor floating bar. Visible once the first
                    widget is stored (matches the canvas-area population
                    rule). Wired into the debounce + flush + revert hook
                    which drives optimistic dispatch + PATCH through the
                    race-safe action hook from Phase 2. */}
                {hasWidgets && selectedDashboard && (
                  <DashboardThemeEditor
                    currentColors={
                      selectedDashboard.presentation?.colors ?? null
                    }
                    onChange={themeEditor.applyDraft}
                    onReset={themeEditor.reset}
                  />
                )}
              </div>

              <div
                className={cn(styles[`${cssClassName}__right-column`], {
                  [styles[`${cssClassName}__right-column--collapsed`]]:
                    isLibraryCollapsed,
                })}
              >
                <WidgetLibraryPanel
                  isCollapsed={isLibraryCollapsed}
                  onToggleCollapse={() =>
                    setIsLibraryCollapsed((prev) => !prev)
                  }
                />
              </div>
            </>
          </div>
        </div>
      </RoundBorderComponentContainer>
    </MainContainer>
  );
};
